import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { RAIL_TYPES, STAGES, featuredPath, subcategoryPath } from './navData';
import TypeIcon from './TypeIcon';
import './MobileMenu.css';

const Chevron = () => (
    <svg className="gh-mmenu-chev" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="9 5 16 12 9 19" />
    </svg>
);

const UserIcon = () => (
    <svg viewBox="0 0 24 24" width="30" height="30" aria-hidden="true">
        <circle cx="12" cy="12" r="11" fill="currentColor" />
        <circle cx="12" cy="9.5" r="3.6" fill="#fff" />
        <path d="M5.6 18.6c1.3-2.6 3.7-4 6.4-4s5.1 1.4 6.4 4" fill="#fff" />
    </svg>
);

// Full-screen mobile menu opened from the header's hamburger. The first screen
// lists the same categories as the desktop side rail, then the account rows.
// Tapping a category drills into it: its "All …" page, then each subcategory
// with its featured names — the mobile version of the desktop flyout.
const MobileMenu = ({ onClose }) => {
    const navigate = useNavigate();
    const { isLoggedIn, email, isAdmin, isBroker, openLogin, logout } = useAuth();
    const [typeId, setTypeId] = useState(null);
    const type = RAIL_TYPES.find((t) => t.id === typeId) || null;

    // Lock page scroll behind the menu; Escape steps back, then closes.
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            if (typeId) setTypeId(null);
            else onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [typeId, onClose]);

    const go = (path) => {
        onClose();
        navigate(path);
    };

    const rootRows = (
        <>
            <ul className="gh-mmenu-list">
                {RAIL_TYPES.map((t) => (
                    <li key={t.id}>
                        <button type="button" className="gh-mmenu-row" onClick={() => setTypeId(t.id)}>
                            <span className="gh-mmenu-icon"><TypeIcon type={t.id} /></span>
                            <span className="gh-mmenu-label">{t.label}</span>
                            <Chevron />
                        </button>
                    </li>
                ))}
            </ul>

            {isLoggedIn ? (
                <ul className="gh-mmenu-list gh-mmenu-account">
                    <li className="gh-mmenu-row gh-mmenu-who">
                        <span className="gh-mmenu-icon">
                            <span className="gh-mmenu-avatar">{(email || '?').charAt(0).toUpperCase()}</span>
                        </span>
                        <span className="gh-mmenu-label gh-mmenu-email">{email}</span>
                    </li>
                    {isBroker && (
                        <li>
                            <button type="button" className="gh-mmenu-row" onClick={() => go('/brokers')}>
                                <span className="gh-mmenu-label">Broker Portal</span>
                                <Chevron />
                            </button>
                        </li>
                    )}
                    {isAdmin && (
                        <li>
                            <button type="button" className="gh-mmenu-row" onClick={() => go('/admin')}>
                                <span className="gh-mmenu-label">Admin</span>
                                <Chevron />
                            </button>
                        </li>
                    )}
                    <li>
                        <button type="button" className="gh-mmenu-row gh-mmenu-logout" onClick={() => { onClose(); logout(); }}>
                            <span className="gh-mmenu-label">Log Out</span>
                        </button>
                    </li>
                </ul>
            ) : (
                <ul className="gh-mmenu-list gh-mmenu-account">
                    <li>
                        <button type="button" className="gh-mmenu-row" onClick={() => { onClose(); openLogin(); }}>
                            <span className="gh-mmenu-icon gh-mmenu-usericon"><UserIcon /></span>
                            <span className="gh-mmenu-label">Log In</span>
                        </button>
                    </li>
                </ul>
            )}
        </>
    );

    const typeRows = type && (
        <>
            {type.route && (
                <ul className="gh-mmenu-list">
                    <li>
                        <button type="button" className="gh-mmenu-row" onClick={() => go(type.route)}>
                            <span className="gh-mmenu-icon"><TypeIcon type={type.id} /></span>
                            <span className="gh-mmenu-label">All {type.label}</span>
                            <Chevron />
                        </button>
                    </li>
                </ul>
            )}
            {(type.stageList || STAGES).map((stage) => {
                const names = type.stages[stage.id] || [];
                const browse = subcategoryPath(type, stage);
                if (names.length === 0 && !browse) return null;
                return (
                    <section key={stage.id} className="gh-mmenu-section">
                        <div className="gh-mmenu-sectionhead">
                            <h3>{stage.label}</h3>
                            {browse && (
                                <button type="button" className="gh-mmenu-seeall" onClick={() => go(browse)}>
                                    See All
                                </button>
                            )}
                        </div>
                        <ul className="gh-mmenu-list">
                            {names.map((name) => (
                                <li key={name}>
                                    <button type="button" className="gh-mmenu-row gh-mmenu-sub" onClick={() => go(featuredPath(type, name))}>
                                        <span className="gh-mmenu-label">{name}</span>
                                        <Chevron />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </section>
                );
            })}
        </>
    );

    return (
        <div className="gh-mmenu" role="dialog" aria-modal="true" aria-label="Menu">
            <div className="gh-mmenu-head">
                {type ? (
                    <button type="button" className="gh-mmenu-back" onClick={() => setTypeId(null)}>
                        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <polyline points="15 5 8 12 15 19" />
                        </svg>
                        {type.label}
                    </button>
                ) : (
                    <div className="gh-mmenu-brand">
                        <Link to="/" className="gh-mmenu-logo" onClick={onClose}>Gradies</Link>
                        <span className="gh-mmenu-title">Menu</span>
                    </div>
                )}
                <button type="button" className="gh-mmenu-close" aria-label="Close menu" onClick={onClose}>
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                        <line x1="5" y1="5" x2="19" y2="19" />
                        <line x1="19" y1="5" x2="5" y2="19" />
                    </svg>
                </button>
            </div>

            <nav className="gh-mmenu-body" aria-label={type ? type.label : 'Categories'}>
                {type ? typeRows : rootRows}
            </nav>
        </div>
    );
};

export default MobileMenu;
